import { gql } from '@apollo/client';

export const ALL_QUOTES_QUERY = gql`
  query AllQuotes($domainId: Int!) {
    allQuotes(condition: { domainId: $domainId }, orderBy: CREATED_AT_DESC) {
      nodes {
        id
        name
        status
        createdAt
        dueAt
        notes
        qbEstimateId
        qbSyncedAt
        qbEmailStatus
        jobByJobId {
          id
          name
          customerByCustomerId {
            id
            name
          }
        }
        addressByBillToAddressId {
          id
          street
          city
          state
          zip
        }
        addressByShipToAddressId {
          id
          street
          city
          state
          zip
        }
        quoteLinesByQuoteId {
          nodes {
            archivedAt
            quoteLineItemsByQuoteLineId {
              nodes {
                quantity
                unitPrice
                archivedAt
              }
            }
          }
        }
      }
    }
  }
`;

export const QUOTE_BY_ID_QUERY = gql`
  query QuoteById($id: Int!) {
    quoteById(id: $id) {
      id
      name
      status
      createdAt
      dueAt
      followUpAt
      notes
      domainId
      jobId
      billToAddressId
      shipToAddressId
      createdBy
      draftQuoteId
      qbEstimateId
      qbSyncedAt
      qbEmailStatus
      lineItemsUpdatedAt
      jobByJobId {
        id
        name
        addressByAddressId {
          id
          street
          city
          state
          zip
        }
        customerByCustomerId {
          id
          name
          email
          phone
          addressesByCustomerId {
            nodes {
              id
              street
              city
              state
              zip
            }
          }
        }
      }
      addressByBillToAddressId {
        id
        street
        city
        state
        zip
      }
      addressByShipToAddressId {
        id
        street
        city
        state
        zip
      }
      draftQuoteByDraftQuoteId {
        id
        draftQuoteObjectsByDraftQuoteId(orderBy: SORT_ORDER_ASC) {
          nodes {
            id
            name
            itemType
            quantity
            widthInches
            heightInches
            depthInches
            fabricDescription
            liningType
            bumpInterlining
            interliningType
            operatingSystem
            hardwareType
            trimPlacement
            trimDescription
            mountType
            rollDirection
            driveSide
            chainColor
            mechanism
            fasciaSize
            inventoryItemId
            rodFaceInches
            overlapInches
            returnInches
            hemSize
            pleatType
            spaceAboveWindowInches
            flapSizeInches
            weltType
            contrastWeltFabricDescription
            insertType
            ruffle
            shamSize
            dropInches
            bedSize
            dustskirtStyle
            fabricWidthInches
            patternRepeatInches
            duvetInsert
            seatHeightInches
            seatDepthInches
            armHeightInches
            backHeightInches
            legHeightInches
            cushionThicknessInches
            armThicknessInches
            seatDeckThicknessInches
            backCushionThicknessInches
            legProfile
            furnitureType
            benchType
            benchShape
            benchStyle
            storageOption
            channelSize
            pillowTopSize
            legSize
            legStyle
            legFinish
            legPriceCents
            trimTop
            trimBottom
            nailheadTop
            nailheadBottom
            nailheadColor
            corniceShape
            corniceOverProduct
            nailheadSides
            nailheadFace
            weltTop
            weltBottom
            weltSides
            weltFace
            bedConfig
            headboardStyle
            headboardShaped
            bedEdgeTreatment
            trimSides
            trimFace
            mattressLengthInches
            mattressWidthInches
            frameThicknessInches
            sideRailFootboardHeightInches
            sideRailFootboardStyleDescription
            bedDrawersSmallQty
            bedDrawersLargeQty
            bedTrundle
            bedFeetType
            bedFeetHeightInches
            bedFeetFinish
            bedFeetSpecialtyDescription
            bedFeetSpecialtyPriceCents
            bedBunkyBoards
            bedBunkyBoardWidthInches
            includeMeasureCharge
            notes
            archivedAt
            draftQuoteObjectItemsByDraftQuoteObjectId(orderBy: ID_ASC) {
              nodes {
                id
                section
                description
                quantity
                unitPrice
                archivedAt
              }
            }
            draftQuoteObjectImagesByDraftQuoteObjectId(orderBy: CREATED_AT_ASC) {
              nodes {
                id
                storedFilename
                originalFilename
                archivedAt
              }
            }
          }
        }
      }
      quoteImagesByQuoteId(orderBy: CREATED_AT_ASC) {
        nodes {
          id
          storedFilename
          originalFilename
          archivedAt
        }
      }
      workOrdersByQuoteId(orderBy: CREATED_AT_DESC) {
        nodes {
          id
          description
          travelerNumber
          type
          status
          createdAt
          dueAt
          archivedAt
          userByAssignedTo {
            id
            username
          }
        }
      }
      quoteLinesByQuoteId(orderBy: LINE_NUMBER_ASC) {
        nodes {
          id
          lineNumber
          section
          archivedAt
          quoteLineItemsByQuoteLineId {
            nodes {
              id
              quantity
              description
              unitPrice
              productType
              notes
              inventoryItemId
              archivedAt
              draftQuoteObjectItemId
              draftQuoteObjectItemByDraftQuoteObjectItemId {
                id
                draftQuoteObjectByDraftQuoteObjectId {
                  id
                  name
                  widthInches
                  heightInches
                  fabricDescription
                  liningType
                  bumpInterlining
                  interliningType
                  operatingSystem
                  hardwareType
                  itemType
                  mountType
                  rollDirection
                  driveSide
                  chainColor
                  flapSizeInches
                  rodFaceInches
                  overlapInches
                  returnInches
                  hemSize
                  pleatType
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const CREATE_QUOTE_MUTATION = gql`
  mutation CreateQuote($domainId: Int!, $jobId: Int!, $name: String!, $billToAddressId: Int, $shipToAddressId: Int, $status: QuoteStatusEnum!, $dueAt: Datetime, $notes: String, $createdBy: Int!) {
    createQuote(input: { quote: { domainId: $domainId, jobId: $jobId, name: $name, billToAddressId: $billToAddressId, shipToAddressId: $shipToAddressId, status: $status, dueAt: $dueAt, notes: $notes, createdBy: $createdBy } }) {
      quote {
        id
        name
        jobId
      }
    }
  }
`;

export const UPDATE_QUOTE_MUTATION = gql`
  mutation UpdateQuote($id: Int!, $billToAddressId: Int, $shipToAddressId: Int, $notes: String) {
    updateQuoteById(
      input: {
        id: $id
        quotePatch: { billToAddressId: $billToAddressId, shipToAddressId: $shipToAddressId, notes: $notes }
      }
    ) {
      quote {
        id
      }
    }
  }
`;

export const CREATE_QUOTE_LINE_MUTATION = gql`
  mutation CreateQuoteLine($quoteId: Int!, $lineNumber: Int!, $section: String!) {
    createQuoteLine(input: { quoteLine: { quoteId: $quoteId, lineNumber: $lineNumber, section: $section } }) {
      quoteLine {
        id
        lineNumber
        section
      }
    }
  }
`;

export const UPDATE_QUOTE_LINE_ITEM_MUTATION = gql`
  mutation UpdateQuoteLineItem($id: Int!, $quantity: BigFloat!, $description: String!, $unitPrice: BigInt!, $productType: ProductTypeEnum) {
    updateQuoteLineItemById(input: {
      id: $id
      quoteLineItemPatch: { quantity: $quantity, description: $description, unitPrice: $unitPrice, productType: $productType }
    }) {
      quoteLineItem { id }
    }
  }
`;

export const UPDATE_QUOTE_LINE_ITEM_PRODUCT_TYPE_MUTATION = gql`
  mutation UpdateQuoteLineItemProductType($id: Int!, $productType: ProductTypeEnum) {
    updateQuoteLineItemById(input: {
      id: $id
      quoteLineItemPatch: { productType: $productType }
    }) {
      quoteLineItem { id }
    }
  }
`;

export const UPDATE_QUOTE_STATUS_MUTATION = gql`
  mutation UpdateQuoteStatus($id: Int!, $status: QuoteStatusEnum!) {
    updateQuoteById(input: { id: $id, quotePatch: { status: $status } }) {
      quote {
        id
        status
      }
    }
  }
`;

export const ARCHIVE_QUOTE_LINE_MUTATION = gql`
  mutation ArchiveQuoteLine($id: Int!, $archivedAt: Datetime!) {
    updateQuoteLineById(input: {
      id: $id
      quoteLinePatch: { archivedAt: $archivedAt }
    }) {
      quoteLine { id }
    }
  }
`;

export const ARCHIVE_QUOTE_LINE_ITEM_MUTATION = gql`
  mutation ArchiveQuoteLineItem($id: Int!, $archivedAt: Datetime!) {
    updateQuoteLineItemById(input: {
      id: $id
      quoteLineItemPatch: { archivedAt: $archivedAt }
    }) {
      quoteLineItem { id }
    }
  }
`;

export const CREATE_QUOTE_LINE_ITEM_MUTATION = gql`
  mutation CreateQuoteLineItem($quoteLineId: Int!, $quantity: BigFloat!, $description: String!, $unitPrice: BigInt!, $notes: String) {
    createQuoteLineItem(input: { quoteLineItem: { quoteLineId: $quoteLineId, quantity: $quantity, description: $description, unitPrice: $unitPrice, notes: $notes } }) {
      quoteLineItem {
        id
        quantity
        description
        unitPrice
        notes
      }
    }
  }
`;

export const ARCHIVE_QUOTE_IMAGE_MUTATION = gql`
  mutation ArchiveQuoteImage($id: Int!, $archivedAt: Datetime!) {
    updateQuoteImageById(input: {
      id: $id
      quoteImagePatch: { archivedAt: $archivedAt }
    }) {
      quoteImage { id }
    }
  }
`;
